<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useApi } from "@/services/api";
import type { OnboardingStepEmits } from "@/views/onboarding/types";

const emit = defineEmits<OnboardingStepEmits>();

const { workspaceId } = defineProps<{
  workspaceId: number;
}>();

const api = useApi();

const firstEmailInput = useTemplateRef<InstanceType<typeof TextInput>>("firstEmailInput");

const firstEmail = ref<string>("");
const emails = ref<string[]>([]);
const isLoading = ref(false);

const isFirstEmailValid = computed(
  () => firstEmail.value.length > 0 && firstEmailInput.value?.checkValidity()
);

function addEmail() {
  if (isFirstEmailValid.value) {
    emails.value.unshift(firstEmail.value);
    firstEmail.value = "";
  }
}

async function invite() {
  isLoading.value = true;
  const toBeInvited = [...emails.value];
  if (isFirstEmailValid.value) {
    toBeInvited.unshift(firstEmail.value);
  }
  if (await api.inviteWorkspaceMembers(workspaceId, toBeInvited)) {
    emit("continue", { workspaceId });
  }
  isLoading.value = false;
}
</script>

<template>
  <div class="onboarding-invite-step">
    <div class="emails">
      <TextInput
        class="first"
        v-model="firstEmail"
        placeholder="member@team.org"
        type="email"
        pattern="^[^@\s]+@[^@\s]+\.[^@\s]+$"
        ref="firstEmailInput"
        @keyup.enter="addEmail"
        :disabled="isLoading"
      />

      <div class="emails-list">
        <TransitionGroup name="list">
          <div v-for="(_, index) in emails" :key="index" class="email">
            <TextInput v-model="emails[index]" type="email" />
            <SimpleButton
              icon-left="icon-trash"
              @click="emails.splice(index, 1)"
              kind="ghost"
              :disabled="isLoading"
            />
          </div>
        </TransitionGroup>
      </div>

      <div
        class="add text-base-regular"
        @click="addEmail"
        :class="{ disabled: !isFirstEmailValid }"
      >
        <i class="icon icon-plus-circle" />
        Add another
      </div>
    </div>

    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loader">
        <SkoreSpinner />
      </div>

      <div v-else class="actions">
        <SimpleButton label="Skip for now" @click="emit('skip')" kind="ghost" />
        <SimpleButton
          label="Invite and continue"
          @click="invite"
          :disabled="emails.length === 0 && !isFirstEmailValid"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.onboarding-invite-step {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-32);

  .loader {
    display: flex;
    height: var(--numbers-40);
    align-items: center;
    justify-content: center;

    .skore-spinner {
      height: 100%;
    }
  }

  .actions {
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: var(--numbers-16);

    .skore-button {
      flex: 1;
    }
  }

  .emails {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);

    .emails-list {
      display: flex;
      flex-direction: column;
      gap: var(--numbers-8);
    }

    .text-input {
      flex: 1;
    }

    .first,
    .email {
      background-color: var(--background-secondary);
    }

    .email {
      display: flex;
      flex-direction: row;
      gap: var(--numbers-8);

      .skore-button {
        background-color: transparent;
        color: var(--content-negative);
      }
    }
  }

  .add {
    cursor: pointer;

    .icon {
      font-size: var(--numbers-20);
      vertical-align: text-bottom;
    }

    &.disabled {
      color: var(--content-disabled);
      cursor: not-allowed;
    }
  }
}
</style>
